
import useConfirmService from "@/composables/useConfirmService";
import useToastService from "@/composables/useToastService";
import {
  GetOrganisationsDocument,
  GetOrganisationsQuery,
  OrganizationType,
  ProfileType,
  AddRoleAssigneeInputType,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useAddRoleAssigneeMutation,
  useRemoveRoleAssigneeMutation,
  GetOrganizationQueryVariables,
  GetOrganizationQuery,
  GetOrganizationDocument,
} from "@/graphql/types";
import { QueryOptions } from "@apollo/client/core";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import gql from "graphql-tag";
import {
  defineComponent,
  PropType,
  reactive,
  ref,
  watch,
  computed,
  nextTick,
} from "vue";
import { useRouter } from "vue-router";
import VDButton from "../VDButton.vue";
import Error from "../Error.vue";
import RolePicker from "@/components/pickers/RolePicker.vue";
import RoleProfilePicker from "@/components/pickers/RoleProfilePicker.vue";
import useUser from "@/composables/useUser";
import {
  UKENDT_ORGANIZATION,
  UKENDT_ORGANIZATION_EU,
  UKENDT_ORGANIZATION_NON_EU,
} from "@/constants";
import useError from "@/composables/useError";
gql`
  mutation CreateOrganization($input: CreateOrganizationInputType!) {
    createOrganization(input: $input) {
      ...OrganizationParts
    }
  }
`;

gql`
  mutation DeleteOrganization($organizationId: ID!) {
    deleteOrganization(organizationId: $organizationId)
  }
`;

gql`
  mutation UpdateOrganization($input: UpdateOrganizationInputType!) {
    updateOrganization(input: $input) {
      ...OrganizationParts
    }
  }
`;

gql`
  query GetProfilesByLevelOfUse($levelOfUse: LevelOfUse!) {
    profilesByLevelOfUse(levelOfUse: $levelOfUse) {
      profileId
      name
      description
      levelOfUse
      application {
        applicationId
        name
      }
      roles {
        roleId
        name
        application {
          applicationId
          name
        }
      }
    }
  }
`;

export default defineComponent({
  components: { VDButton, Error, RolePicker, RoleProfilePicker },
  emits: ["organization-updated", "updated"],
  props: {
    organization: {
      type: Object as PropType<OrganizationType | null>,
      default: null,
    },
  },
  setup(props, { emit }) {
    const confirmService = useConfirmService();
    const router = useRouter();
    const toastService = useToastService();
    const isVisible = ref(false);
    const nameInput = ref();
    const newOrganization = reactive<OrganizationType>({
      name: "",
      vat: "",
      assigneeRoles: [],
      notifiedOnChange: true,
      notifiedOnUserChange: true,
      notifiedOnRoleAssignmentChange: true,
    });

    // Pass the entire reactive object to Vuelidate for reactivity
    const v$ = useVuelidate(
      useValidations(),
      computed(() => ({
        name: newOrganization.name ?? "",
        vat: newOrganization.vat ?? "",
      }))
    );
    const isNewOrganization = computed(() => !props.organization);

    const hasChanges = computed(() => {
      if (isNewOrganization.value) {
        return newOrganization.name != "" && newOrganization.vat != "";
      } else {
        return (
          newOrganization.name &&
          newOrganization.vat &&
          (newOrganization.name != props.organization?.name ||
            newOrganization.vat != props.organization?.vat ||
            newOrganization.notifiedOnChange !=
              props.organization?.notifiedOnChange ||
            newOrganization.notifiedOnUserChange !=
              props.organization?.notifiedOnUserChange ||
            newOrganization.notifiedOnRoleAssignmentChange !=
              props.organization?.notifiedOnRoleAssignmentChange)
        );
      }
    });
    const { user } = useUser();

    const canManageOrganizationRoles = computed(
      () =>
        !!(
          user.value?.isSuperAdmin ||
          user.value?.isUserAdmin ||
          user.value?.isApplicationAdmin
        )
    );

    const canManageOrg = computed(() => !!user.value?.isSuperAdmin);

    const deleteOrganizationMutation = useDeleteOrganizationMutation({});
    const createOrganizationMutation = useCreateOrganizationMutation({});
    const updateOrganizationMutation = useUpdateOrganizationMutation({});

    function deleteOrganization(event: Event) {
      function callMutation() {
        if (newOrganization.organizationId)
          deleteOrganizationMutation.mutate(
            { organizationId: newOrganization.organizationId },
            {
              refetchQueries: [{ query: GetOrganisationsDocument }],
            }
          );
      }

      confirmService.show(
        event,
        `Organisationen indeholder ${props.organization?.users?.total} brugere.
          <br/> <br/>
          Handlingen kan ikke fortrydes. <br/>
          Er du sikker?`,
        "Ja, slet",
        "Afbryd",
        () => {
          callMutation();
        }
      );
    }

    function primaryButtonPressed() {
      v$.value.$touch();
      if (!v$.value.$invalid)
        if (props.organization && props.organization.organizationId) {
          updateOrganizationMutation.mutate(
            {
              input: {
                organizationId: newOrganization.organizationId,
                name: newOrganization.name,
                vat: newOrganization.vat,
                notifiedOnChange: newOrganization.notifiedOnChange,
                notifiedOnUserChange: newOrganization.notifiedOnUserChange,
                notifiedOnRoleAssignmentChange:
                  newOrganization.notifiedOnRoleAssignmentChange,
              },
            },
            {
              update: (cache, { data: mutationResult }) => {
                const parentQuery: QueryOptions<
                  GetOrganizationQueryVariables,
                  GetOrganizationQuery
                > = {
                  query: GetOrganizationDocument,
                  variables: {
                    organizationId: props.organization?.organizationId,
                    pageParams: { pageSize: 10, currentPage: 1 },
                  },
                };

                const cachedData = cache.readQuery(parentQuery);

                cache.writeQuery({
                  ...parentQuery,
                  data: {
                    organization: {
                      ...cachedData?.organization,
                      name: mutationResult?.updateOrganization?.name,
                      vat: mutationResult?.updateOrganization?.vat,
                      notifiedOnChange:
                        mutationResult?.updateOrganization?.notifiedOnChange,
                      notifiedOnUserChange:
                        mutationResult?.updateOrganization
                          ?.notifiedOnUserChange,
                      notifiedOnRoleAssignmentChange:
                        mutationResult?.updateOrganization
                          ?.notifiedOnRoleAssignmentChange,
                    },
                  },
                });
              },
            }
          );
        } else {
          createOrganizationMutation.mutate(
            {
              input: {
                name: newOrganization.name,
                vat: newOrganization.vat,
                notifiedOnChange: newOrganization.notifiedOnChange,
                notifiedOnUserChange: newOrganization.notifiedOnUserChange,
                notifiedOnRoleAssignmentChange:
                  newOrganization.notifiedOnRoleAssignmentChange,
                assigneeRoles: newOrganization.assigneeRoles,
              },
            },
            {
              refetchQueries: [
                {
                  query: GetOrganisationsDocument,
                },
              ],
              awaitRefetchQueries: true,
            }
          );
        }
    }

    createOrganizationMutation.onDone((result) => {
      // Add roles to the newly created organization if any were selected
      if (
        result.data?.createOrganization?.organizationId &&
        newOrganization.assigneeRoles?.length
      ) {
        const orgId = result.data.createOrganization.organizationId;
        const roleIds = newOrganization.assigneeRoles
          .map((r) => r?.roleId)
          .filter(Boolean);

        // Add each role individually
        roleIds.forEach((roleId) => {
          if (roleId) {
            addRoleAssigneeMutation.mutate({
              input: {
                organizationId: orgId,
                roleId: roleId,
              },
            });
          }
        });
      }

      v$.value.$reset();
      Object.assign(newOrganization, {
        name: "",
        vat: "",
        organizationId: "",
        assigneeRoles: [],
        notifiedOnChange: true,
        notifiedOnUserChange: true,
        notifiedOnRoleAssignmentChange: true,
      });
      isVisible.value = false;

      toastService.show({
        severity: "success",
        title: "Organisation oprettet",
        message: `Den har fået ID: ${result.data?.createOrganization?.organizationId}`,
      });
    });

    createOrganizationMutation.onError((error) => {
      toastService.show({
        severity: "error",
        error: useError(error, "Fejl ved oprettelse af organisation"),
      });
    });

    updateOrganizationMutation.onDone((result) => {
      toastService.show({
        severity: "success",
        title: "Organisationen opdateret",
        message: "Dine ændringer er blevet gemt",
      });
      isVisible.value = false;
      // Emit event to parent with updated organization data
      if (result?.data?.updateOrganization) {
        // Use $emit if using Options API, or context.emit if using setup()
        // Here, we use emit from setup context
        emit && emit("organization-updated", result.data.updateOrganization);
      }
    });

    updateOrganizationMutation.onError((error) => {
      toastService.show({
        severity: "error",
        error: useError(error, "Fejl ved opdatering af organisation"),
      });
    });

    deleteOrganizationMutation.onDone(() => {
      toastService.show({
        severity: "success",
        title: "Organisation slettet",
        message: `Organisationen er blevet slettet`,
      });
      router.push("/organizations");
    });

    deleteOrganizationMutation.onError((error) => {
      toastService.show({
        severity: "error",
        error: useError(error, "Fejl ved sletning af organisation"),
      });
    });

    watch(
      () => props.organization,
      (newValue) => {
        if (newValue) {
          // For existing organizations, use their actual values
          Object.assign(newOrganization, newValue);
        } else {
          // For new organizations, reset to defaults with true values for notifications
          Object.assign(newOrganization, {
            name: "",
            vat: "",
            organizationId: "",
            assigneeRoles: [],
            notifiedOnChange: true,
            notifiedOnUserChange: true,
            notifiedOnRoleAssignmentChange: true,
          });
        }
      },
      { immediate: true }
    );

    // Role Management
    const rolePicker = ref<any>();
    const roleProfilePicker = ref<any>();
    const currentRoleBeingRemoved = ref<AddRoleAssigneeInputType | null>(null);
    const roleQueueProcessing = ref(false);
    const rolesToAddQueue = ref<any[]>([]);

    const addRoleAssigneeMutation = useAddRoleAssigneeMutation({});
    const removeRoleAssigneeMutation = useRemoveRoleAssigneeMutation({});

    // Computed loading state - disables picker while any mutation is in progress
    const addRemoveLoading = computed(
      () =>
        addRoleAssigneeMutation.loading.value ||
        removeRoleAssigneeMutation.loading.value ||
        roleQueueProcessing.value
    );

    function addRole(role: any) {
      // Transform RoleType to AddRoleAssigneeInputType structure for display
      const roleAssignee: AddRoleAssigneeInputType = {
        roleId: role.roleId,
        roleName: role.name,
        applicationName: role.application?.name || "Unknown Application",
        organizationId: props.organization?.organizationId,
        organizationName: props.organization?.name || "",
      };

      // For new organizations, just add to local array
      if (!props.organization?.organizationId) {
        if (
          !newOrganization.assigneeRoles?.some(
            (r) => r?.roleId === roleAssignee.roleId
          )
        ) {
          newOrganization.assigneeRoles = [
            ...(newOrganization.assigneeRoles || []),
            roleAssignee,
          ];
        }
        return;
      }

      // For existing organizations, call mutation and handle result
      addRoleAssigneeMutation
        .mutate({
          input: {
            organizationId: props.organization.organizationId,
            roleId: role.roleId!,
          },
        })
        .then((result) => {
          if (result?.data?.addRoleAssignee) {
            // Add role to UI
            if (
              !newOrganization.assigneeRoles?.some(
                (existingRole) =>
                  existingRole?.roleId === roleAssignee.roleId &&
                  existingRole?.organizationId === roleAssignee.organizationId
              )
            ) {
              newOrganization.assigneeRoles = [
                ...(newOrganization.assigneeRoles || []),
                roleAssignee,
              ];
            }

            toastService.show({
              severity: "success",
              title: "Rolle tilføjet",
              message: "Rollen er blevet tilføjet til organisationen",
            });
            emit("organization-updated");
          }
        })
        .catch((error) => {
          toastService.show({
            severity: "error",
            title: "Fejl",
            message: "Der opstod en fejl ved tilføjelse af rollen",
          });
        });
    }

    function removeRole(role: AddRoleAssigneeInputType) {
      // For new organizations, just remove from local array
      if (!props.organization?.organizationId) {
        newOrganization.assigneeRoles =
          newOrganization.assigneeRoles?.filter(
            (r) => r?.roleId !== role.roleId
          ) || [];
        return;
      }

      // For existing organizations, call mutation
      currentRoleBeingRemoved.value = role;
      removeRoleAssigneeMutation.mutate({
        organizationId: props.organization.organizationId,
        roleId: role.roleId!,
      });
    }

    function addProfileRoles(profile: any) {
      // Add all roles from the selected profile
      if (profile.roles && profile.roles.length > 0) {
        const rolesToAdd = profile.roles.filter((role: any) => {
          // Check if role is not already added
          const existingRole = newOrganization.assigneeRoles?.find(
            (r) => r?.roleId === role.roleId
          );
          return !existingRole;
        });

        if (rolesToAdd.length === 0) {
          toastService.show({
            severity: "info",
            title: "Ingen nye roller",
            message: `Alle roller fra profilen "${profile.name}" er allerede tilføjet`,
          });
          return;
        }

        // For existing organizations, queue the roles for sequential addition
        if (props.organization?.organizationId) {
          rolesToAddQueue.value = rolesToAdd;
          processRoleQueue(profile.name);
        } else {
          // For new organizations, add directly to local array
          rolesToAdd.forEach((role: any) => {
            addRole(role);
          });

          toastService.show({
            severity: "success",
            title: "Rolleprofil tilføjet",
            message: `${rolesToAdd.length} roller fra profilen "${profile.name}" er tilføjet`,
          });
        }
      } else {
        toastService.show({
          severity: "info",
          title: "Ingen roller",
          message: `Profilen "${profile.name}" har ingen roller at tilføje`,
        });
      }
    }

    async function processRoleQueue(profileName: string) {
      if (roleQueueProcessing.value || rolesToAddQueue.value.length === 0) {
        return;
      }

      roleQueueProcessing.value = true;
      let addedCount = 0;
      let errorCount = 0;

      for (const role of rolesToAddQueue.value) {
        try {
          await addRoleToExistingOrganization(role);
          addedCount++;
        } catch (error) {
          errorCount++;
          console.error("Failed to add role:", role.name, error);
        }
      }

      // Clear the queue
      rolesToAddQueue.value = [];
      roleQueueProcessing.value = false;

      // Show result toast
      if (addedCount > 0) {
        toastService.show({
          severity: errorCount > 0 ? "warn" : "success",
          title: "Rolleprofil tilføjet",
          message:
            errorCount > 0
              ? `${addedCount} roller fra profilen "${profileName}" er tilføjet. ${errorCount} roller fejlede.`
              : `${addedCount} roller fra profilen "${profileName}" er tilføjet`,
        });
      } else if (errorCount > 0) {
        toastService.show({
          severity: "error",
          title: "Fejl ved tilføjelse",
          message: `Kunne ikke tilføje roller fra profilen "${profileName}". Prøv igen.`,
        });
      }
    }

    function addRoleToExistingOrganization(role: any): Promise<void> {
      return new Promise((resolve, reject) => {
        const roleAssignee: AddRoleAssigneeInputType = {
          roleId: role.roleId,
          roleName: role.name,
          applicationName: role.application?.name || "Unknown Application",
          organizationId: props.organization?.organizationId || "",
          organizationName: props.organization?.name || "",
        };

        // Create a unique subscription for this specific role addition
        const onDoneUnsubscribe = addRoleAssigneeMutation.onDone((result) => {
          if (result.data?.addRoleAssignee) {
            // Add role to UI
            if (
              !newOrganization.assigneeRoles?.some(
                (existingRole) =>
                  existingRole?.roleId === roleAssignee.roleId &&
                  existingRole?.organizationId === roleAssignee.organizationId
              )
            ) {
              newOrganization.assigneeRoles = [
                ...(newOrganization.assigneeRoles || []),
                roleAssignee,
              ];
            }
          }
          onDoneUnsubscribe.off();
          onErrorUnsubscribe.off();
          resolve();
        });

        const onErrorUnsubscribe = addRoleAssigneeMutation.onError((error) => {
          onDoneUnsubscribe.off();
          onErrorUnsubscribe.off();
          reject(error);
        });

        addRoleAssigneeMutation.mutate({
          input: {
            organizationId: props.organization!.organizationId,
            roleId: role.roleId!,
          },
        });
      });
    }

    // Remove the global mutation handlers since we handle results in individual functions

    removeRoleAssigneeMutation.onDone((result) => {
      if (result.data?.removeRoleAssignee && currentRoleBeingRemoved.value) {
        // Remove the role from the newOrganization's assigneeRoles list
        const roleToRemove = currentRoleBeingRemoved.value;
        if (newOrganization?.assigneeRoles) {
          const index = newOrganization.assigneeRoles.findIndex(
            (role) =>
              role?.roleId === roleToRemove.roleId &&
              role?.organizationId === roleToRemove.organizationId
          );
          if (index > -1) {
            newOrganization.assigneeRoles.splice(index, 1);
          }
        }

        currentRoleBeingRemoved.value = null;
        toastService.show({
          severity: "success",
          title: "Rolle fjernet",
          message: "Rollen er blevet fjernet fra organisationen",
        });
        emit("organization-updated");
      }
    });

    removeRoleAssigneeMutation.onError((error) => {
      currentRoleBeingRemoved.value = null;
      toastService.show({
        severity: "error",
        error: useError(error, "Fejl"),
      });
    });

    // Watch for modal visibility and focus input when it becomes visible
    watch(isVisible, (newValue) => {
      if (newValue) {
        nextTick(() => {
          // Try multiple ways to access and focus the input
          const inputEl =
            nameInput.value?.$el?.querySelector("input") ||
            nameInput.value?.$el ||
            nameInput.value?.$refs?.input ||
            nameInput.value;

          if (inputEl && typeof inputEl.focus === "function") {
            inputEl.focus();
          }
        });
      }
    });

    function isUnknownOrganization(): boolean {
      return (
        props.organization?.vat === UKENDT_ORGANIZATION ||
        props.organization?.vat === UKENDT_ORGANIZATION_EU ||
        props.organization?.vat === UKENDT_ORGANIZATION_NON_EU
      );
    }

    return {
      isVisible,
      nameInput,
      show: () => (isVisible.value = true),
      hide: () => (isVisible.value = false),
      newOrganization,
      v$,
      deleteOrganizationMutation,
      updateOrganizationMutation,
      createOrganizationMutation,
      primaryButtonPressed,
      deleteOrganization,
      canViewOrganizationRoles: canManageOrganizationRoles,
      hasChanges,
      canManageOrg,
      // Role management
      rolePicker,
      roleProfilePicker,
      addRemoveLoading,
      addRole,
      removeRole,
      addProfileRoles,
      isUnknownOrganization,
    };
  },
});

function useValidations() {
  return {
    name: { required },
    vat: { required },
  };
}

export declare class OrganizationModal {
  show(): void;
  hide(): void;
}
